import { useEffect, useMemo, useState, useContext } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import LineEnvelopeIcon from 'LuxKit/Icons/line/LineEnvelopeIcon'
import TextButton from 'LuxKit/Button/TextButton'
import { sendShareReferralLinkRequest } from 'api/notification'
import { addQueryParamsToUrl } from 'lib/url/searchUrlUtils'
import config from 'constants/config'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import ShareModal from 'components/Common/Modals/ShareModal'
import Group from 'components/utils/Group'
import initiateShareThis from 'components/Vendor/ShareThis/init'
import FacebookRoundIcon from 'LuxKit/Icons/custom/FacebookRoundIcon'
import LineFacebookMessengerIcon from 'LuxKit/Icons/line/LineFacebookMessengerIcon'
import { useResponsive } from 'hooks/useResponsive'
import { fetchReferralValue } from 'actions/ReferAction'
import { formatRoundedAmount } from 'lib/format/formatCurrencyIntl'
import { min, max } from 'lib/array/arrayUtils'
import ShareButton from './ShareButton'
import { useAppDispatch } from 'hooks/reduxHooks'
import GeoContext from 'contexts/geoContext'
import { EmptyObject } from 'lib/object/objectUtils'
import { getCurrentIntl } from 'i18n/util/currentIntl'

interface Props {
  regionCode: string
  code: string
  memberId: string
  referralValue?: App.ReferralValue
  currencyCode: string
  utm?: { [param: string]: string }
}

export function getReferralValueString(referralValue: App.ReferralValue | undefined, currencyCode: string, regionCode: string): string | undefined {
  if (!referralValue) {
    return undefined
  }
  const discounts = Object.values(referralValue.product_type_discount)

  if (discounts.length > 0) {
    const { value: maxDiscount } = max(discounts, item => item.value)!
    const { value: minDiscount } = min(discounts, item => item.value)!

    let str

    if (referralValue.type === 'fixed_amount') {
      str = formatRoundedAmount(maxDiscount, currencyCode, regionCode)
    }

    if (str) {
      if (minDiscount !== maxDiscount) {
        const intl = getCurrentIntl()
        str = intl.formatMessage(
          {
            id: '93u3_VP4hdiitollQ_BR',
            defaultMessage: 'up to {amount}',
            description: 'Referral discount prefix when min and max discounts differ, shown in share buttons on the refer page',
          },
          { amount: str },
        )
      }

      return str
    }
  }
}

function ShareButtons(props: Props) {
  const intl = useIntl()
  const { code, regionCode, memberId, referralValue, currencyCode, utm = EmptyObject } = props

  const { currentRegionCode } = useContext(GeoContext)

  const referralValueString = getReferralValueString(referralValue, currencyCode, currentRegionCode)
  const [showButtonText, setShowButtonText] = useState<boolean>(false)
  const [sizes] = useResponsive()
  const dispatch = useAppDispatch()

  useEffect(() => {
    initiateShareThis()
  }, [])

  useEffect(() => {
    dispatch(fetchReferralValue(regionCode))
  }, [dispatch, regionCode])

  useEffect(() => {
    setShowButtonText(!!(sizes.tablet || sizes.desktop || sizes.largeDesktop))
  }, [sizes])

  const shareConfig = useMemo(() => {
    const shareTitle = referralValueString ?
        intl.formatMessage(
          {
            id: 'nRkQGz4kJ2H72_Oc_KpU',
            defaultMessage: 'You can save {referralValue} off your first booking with {brand}',
            description: 'Share title for referral with discount amount on the marketing page',
          },
          { referralValue: referralValueString, brand: config.BRAND },
        ) :
        intl.formatMessage(
          {
            id: 'RrfjfhrbzIAeuUzr-sip',
            defaultMessage: 'Save on your first booking with {brand}',
            description: 'Share title for referral without discount amount on the marketing page',
          },
          { brand: config.BRAND },
        )
    return {
      url: addQueryParamsToUrl(`${config.SCHEME}://${config.CUSTOMER_PORTAL_HOST}/refer/${code}`, utm),
      utm_campaign: utm?.utm_campaign ?? 'referral',
      shareTitle,
      mailToSubject: '',
      pageId: `/refer/${code}`,
      pageType: 'refer-a-friend',
      onSend: ({ friend_email }: { friend_email: string }) => {
        return sendShareReferralLinkRequest({
          referralCustomerId: memberId,
          friendEmail: friend_email,
          region: regionCode,
          utm,
        })
      },
      shareMessage: '',
    }
  }, [regionCode, memberId, code, referralValueString, utm, intl])

  const moreText = intl.formatMessage({
    id: 'OHZMLgw9_ogxbdMidWQJ',
    defaultMessage: 'More',
    description: 'Button text to show more sharing options for referral',
  })

  return (
    <Group direction="horizontal">
      <ShareModal shareConfig={shareConfig} mode="email">
        <ShareButton
          label={intl.formatMessage({
            id: 'I8DoghSFZznSazLbSjG4',
            defaultMessage: 'Email',
            description: 'Share button label for email referral sharing on the marketing page',
          })}
          icon={<LineEnvelopeIcon />}
          isIconOnly={!showButtonText}
        />
      </ShareModal>
      <ShareButton
        label={intl.formatMessage({
          id: '-F8eZdDKjYQYPdSaGHge',
          defaultMessage: 'Facebook',
          description: 'Share button label for Facebook referral sharing on the marketing page',
        })}
        icon={<FacebookRoundIcon />}
        isIconOnly={!showButtonText}
        className="st-custom-button"
        data-network="facebook"
        data-url={shareConfig.url}
        data-title={shareConfig.shareTitle}
        data-description={shareConfig.shareMessage}
      />
      <ShareButton
        label={intl.formatMessage({
          id: 'qTqvqBlj0XnX0gqFfZov',
          defaultMessage: 'Messenger',
          description: 'Share button label for Facebook Messenger referral sharing',
        })}
        icon={<LineFacebookMessengerIcon />}
        isIconOnly={!showButtonText}
        className="st-custom-button"
        data-network="messenger"
        data-url={shareConfig.url}
        data-title={shareConfig.shareTitle}
        data-description={shareConfig.shareMessage}
      />
      <ShareModal shareConfig={shareConfig}>
        <TextButton kind="tertiary" startIcon={<LinePlusIcon />} variant="ghost">
          {moreText}
        </TextButton>
      </ShareModal>
    </Group>
  )
}

const mapState = (state: App.State) => ({
  regionCode: state.geo.currentRegionCode,
  currencyCode: state.geo.currentCurrency,
  memberId: state.auth.account.memberId,
  referralValue: state.refer.referralValue,
})

const mapDispatchToProps = {
  fetchReferralValue,
}

export default connect(mapState, mapDispatchToProps)(ShareButtons)
